import { v4 as uuidv4 } from "uuid";
import { MoonLoader } from "react-spinners";
import { DataRowCard } from "./components/DataRowCard";
import { PaneManager } from "./components/PaneManager";
import { DateRangeFilter, DateRange } from "./components/DateRangeFilter";
import React, { useCallback, useEffect, useRef, useState } from "react";
import apiUtils from "../../../apiUtils";
import logger from "../../../utils/logger";
import { ClinicalMeasurement, PatientAgeInfo } from "../../../types";
import {
  computeVanderbiltStatus,
  isVanderbiltApplicable,
} from "../../../utils/vanderbiltUtils";
import {
  computeScreenerStatus,
  PHQ9_DIAGNOSIS_CODES,
  GAD7_DIAGNOSIS_CODES,
  isScreenerApplicable,
} from "../../../utils/screenerUtils";

// Define props interface for MeasurementDashboard
interface MeasurementDashboardProps {
  patientId: string | null;
  nextAppointmentDate?: string | null;
  patientAge?: PatientAgeInfo | null;
}

interface Tab {
  id: string;
  measureName: string;
}

const MeasurementDashboard: React.FC<MeasurementDashboardProps> = ({
  patientId,
  nextAppointmentDate,
  patientAge,
}) => {
  const [openTabs, setOpenTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string>("");
  const [measures, setMeasures] = useState<ClinicalMeasurement[]>([]);
  const [measureDataMap, setMeasureDataMap] = useState<
    Record<string, ClinicalMeasurement>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [diagnosisCodes, setDiagnosisCodes] = useState<string>("");

  const [globalDateRange, setGlobalDateRange] = useState<DateRange>({
    kind: "time",
    start: null,
    end: null,
    label: "All History",
  });

  useEffect(() => {
    const fetchMeasurements = async () => {
      if (!patientId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        let failureCount = 0;
        // Fetch all 4 Vanderbilt endpoints concurrently using apiUtils plus clinical data
        const [
          vanderbiltParentInitial,
          vanderbiltTeacherInitial,
          vanderbiltParentReassessment,
          vanderbiltTeacherReassessment,
          patientContext,
          phq9Data,
          gad7Data,
        ] = await Promise.all([
          apiUtils
            .fetchData(
              `/api/mbc/vanderbilt/parent-initial?patient_id=${patientId}`,
              "GET",
              null,
              {
                uiLogMessage: "Retrieving Parent Initial Assessment",
                logToUI: false,
              },
            )
            .catch((e) => {
              failureCount++;
              logger.warn("Vanderbilt Parent Initial fetch failed", {
                error: e instanceof Error ? e.message : String(e),
              });
              return null;
            }),
          apiUtils
            .fetchData(
              `/api/mbc/vanderbilt/teacher-initial?patient_id=${patientId}`,
              "GET",
              null,
              {
                uiLogMessage: "Retrieving Teacher Initial Assessment",
                logToUI: false,
              },
            )
            .catch((e) => {
              failureCount++;
              logger.warn("Vanderbilt Teacher Initial fetch failed", {
                error: e instanceof Error ? e.message : String(e),
              });
              return null;
            }),
          apiUtils
            .fetchData(
              `/api/mbc/vanderbilt/parent-reassessment?patient_id=${patientId}`,
              "GET",
              null,
              {
                uiLogMessage: "Retrieving Parent Reassessment",
                logToUI: false,
              },
            )
            .catch((e) => {
              failureCount++;
              logger.warn("Vanderbilt Parent Reassessment fetch failed", {
                error: e instanceof Error ? e.message : String(e),
              });
              return null;
            }),
          apiUtils
            .fetchData(
              `/api/mbc/vanderbilt/teacher-reassessment?patient_id=${patientId}`,
              "GET",
              null,
              {
                uiLogMessage: "Retrieving Teacher Reassessment",
                logToUI: false,
              },
            )
            .catch((e) => {
              failureCount++;
              logger.warn("Vanderbilt Teacher Reassessment fetch failed", {
                error: e instanceof Error ? e.message : String(e),
              });
              return null;
            }),
          apiUtils
            .fetchData(
              `/api/mbc/patient-context?patient_id=${patientId}`,
              "GET",
              null,
              {
                uiLogMessage: "Retrieving patient context",
                logToUI: false,
              },
            )
            .catch((e) => {
              failureCount++;
              logger.warn("Patient context fetch failed", {
                error: e instanceof Error ? e.message : String(e),
              });
              return null;
            }),
          apiUtils
            .fetchData(`/api/mbc/phq9?patient_id=${patientId}`, "GET", null, {
              uiLogMessage: "Retrieving PHQ-9 Assessment",
              logToUI: false,
            })
            .catch((e) => {
              failureCount++;
              logger.warn("PHQ-9 fetch failed", {
                error: e instanceof Error ? e.message : String(e),
              });
              return null;
            }),
          apiUtils
            .fetchData(`/api/mbc/gad7?patient_id=${patientId}`, "GET", null, {
              uiLogMessage: "Retrieving GAD-7 Assessment",
              logToUI: false,
            })
            .catch((e) => {
              failureCount++;
              logger.warn("GAD-7 fetch failed", {
                error: e instanceof Error ? e.message : String(e),
              });
              return null;
            }),
        ]);

        if (failureCount === 7) {
          throw new Error("Failed to load dashboard data");
        }

        const fetchedVanderbiltResponses = [
          vanderbiltParentInitial,
          vanderbiltTeacherInitial,
          vanderbiltParentReassessment,
          vanderbiltTeacherReassessment,
        ];

        // Patient age + diagnosis codes drive the per-measure applicability gates.
        // The backend computes age from the single-source DOB; fall back to the
        // (test-injected) patientAge prop when the context fetch returns nothing.
        const context = patientContext as {
          patientAge?: PatientAgeInfo | null;
          diagnosisCodes?: string;
        } | null;
        const effectiveAge = context?.patientAge ?? patientAge ?? null;
        const dCodes = context?.diagnosisCodes ?? "";
        setDiagnosisCodes(dCodes);

        // Apply new Vanderbilt business rules to compute unified status
        const processedMeasures = computeVanderbiltStatus(
          fetchedVanderbiltResponses as unknown as (ClinicalMeasurement | null)[],
          effectiveAge,
        );

        const processedScreenerMeasures = [
          computeScreenerStatus(
            phq9Data as ClinicalMeasurement | null,
            "PHQ-9 Assessment",
            effectiveAge,
            dCodes,
            PHQ9_DIAGNOSIS_CODES,
          ),
          computeScreenerStatus(
            gad7Data as ClinicalMeasurement | null,
            "GAD-7 Assessment",
            effectiveAge,
            dCodes,
            GAD7_DIAGNOSIS_CODES,
          ),
        ];

        // Filter valid measures to display.
        // If not applicable, we only show what exists. If applicable, processedMeasures includes placeholders for missing ones.
        const applicable = isVanderbiltApplicable(effectiveAge);
        const validVanderbiltMeasures = processedMeasures.filter(
          (data) =>
            applicable ||
            (Array.isArray(data.longitudinalData) &&
              data.longitudinalData.length > 0),
        );

        const applicablePhq9 = isScreenerApplicable(
          effectiveAge,
          dCodes,
          PHQ9_DIAGNOSIS_CODES,
        );
        const applicableGad7 = isScreenerApplicable(
          effectiveAge,
          dCodes,
          GAD7_DIAGNOSIS_CODES,
        );

        const validScreenerMeasures = processedScreenerMeasures.filter(
          (data, index) => {
            const isApplicable = index === 0 ? applicablePhq9 : applicableGad7;
            return (
              isApplicable ||
              (Array.isArray(data.longitudinalData) &&
                data.longitudinalData.length > 0)
            );
          },
        );

        const validMeasures = [
          ...validVanderbiltMeasures,
          ...validScreenerMeasures,
        ];

        setMeasures(validMeasures);

        // Create a map of valid measurements, keyed by measureName
        const measureDataMapResult: Record<string, ClinicalMeasurement> =
          validMeasures.reduce(
            (acc, data) => {
              if (data && data.measureName) {
                acc[data.measureName] = data;
              }
              return acc;
            },
            {} as Record<string, ClinicalMeasurement>,
          );

        setMeasureDataMap(measureDataMapResult);
      } catch (err: unknown) {
        logger.error(
          "MeasurementDashboard: failed to fetch clinical measurement data",
          err instanceof Error ? err : new Error(String(err)),
          { patient_id: patientId },
        );
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load dashboard data";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeasurements();
  }, [patientAge, patientId]);

  const handleViewDetails = (measureName: string) => {
    const newTab: Tab = {
      id: `${measureName}-${uuidv4()}`,
      measureName,
    };
    setOpenTabs([...openTabs, newTab]);
    setActiveTabId(newTab.id);
  };

  const handleTabsUpdate = (tabs: Tab[], newActiveTabId: string) => {
    setOpenTabs(tabs);
    setActiveTabId(newActiveTabId);
  };

  // Refs for scroll-sync between the sticky header and the data rows container.
  // The header lives in overflow-x-hidden so sticky top-0 works on page scroll.
  // Its scrollLeft is mirrored from the data rows container on every scroll event.
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const dataScrollRef = useRef<HTMLDivElement>(null);

  const handleDataScroll = useCallback(() => {
    if (headerScrollRef.current && dataScrollRef.current) {
      headerScrollRef.current.scrollLeft = dataScrollRef.current.scrollLeft;
    }
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#F5F7FA" }}
      data-testid="mbc-dashboard"
    >
      <div className="max-w-7xl mx-auto px-10 py-10">
        {/* Header with Title AND Date Picker */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-xl font-bold" style={{ color: "#1A202C" }}>
            Measurement Based Care Dashboard
          </h1>
          <div className="mt-1">
            <h2 className="text-sm font-medium" style={{ color: "#475569" }}>
              Next Appointment: {nextAppointmentDate || "None Scheduled"}
            </h2>
            {diagnosisCodes && (
              <h2
                className="text-sm font-medium mt-1"
                style={{ color: "#475569" }}
              >
                Diagnosis Codes: {diagnosisCodes}
              </h2>
            )}
          </div>
          {/* Date Filter */}
          <DateRangeFilter
            currentRange={globalDateRange}
            onRangeChange={setGlobalDateRange}
          />
        </div>

        {/* Table Container */}
        <div className="flex flex-col gap-3">
          {isLoading ? (
            <div
              className="flex flex-col items-center justify-center p-12 bg-white rounded-lg border border-dashed border-slate-300 shadow-sm"
              data-testid="mbc-loading"
            >
              <MoonLoader
                size={30}
                color="#7a46bb"
                loading={true}
                speedMultiplier={0.75}
              />
              <span className="text-slate-500 font-medium mt-4">
                Loading assessments…
              </span>
            </div>
          ) : error ? (
            <div
              className="flex flex-col items-center justify-center p-12 bg-white rounded-lg border border-dashed border-red-300 shadow-sm"
              data-testid="mbc-error"
              role="alert"
            >
              <span className="text-red-600 font-semibold">
                Unable to load assessments
              </span>
              <span className="text-slate-500 text-sm mt-2 text-center">
                Something went wrong while loading this patient's data. Please
                try again in a moment.
              </span>
            </div>
          ) : measures.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center p-12 bg-white rounded-lg border border-dashed border-slate-300 shadow-sm"
              data-testid="mbc-empty-state"
            >
              <span className="text-slate-500 font-medium">
                No assessments data available
              </span>
            </div>
          ) : (
            /*
              Freeze-pane layout:
              - Outer div is the sticky boundary: the sticky header stops following once
                this div scrolls out of the viewport (it won't bleed into PaneManager).
              - Header sits in its own overflow-x-hidden container (headerScrollRef) with
                sticky top-0. Its scrollLeft is driven by the data rows container so
                columns stay pixel-perfect in sync.
              - Data rows sit in their own overflow-x-auto container (dataScrollRef) that
                fires handleDataScroll to mirror scrollLeft into the header.
              - A transparent 1px border on the header row matches the card's 1px border
                so all grid columns align exactly.
            */
            <div className="relative">
              {/* Sticky column header — outside the data-rows overflow so sticky works on page scroll */}
              <div
                ref={headerScrollRef}
                className="sticky top-0 z-10 overflow-x-hidden"
                style={{ backgroundColor: "#F5F7FA" }}
              >
                <div
                  className="grid grid-cols-[200px_minmax(180px,1fr)_140px_140px_200px] gap-6 px-6 py-3 min-w-[1020px] shadow-sm"
                  style={{
                    border: "1px solid transparent",
                    borderBottomColor: "#E2E8F0",
                  }}
                >
                  <div
                    className="text-sm font-bold"
                    style={{ color: "#334155" }}
                  >
                    Measure Name
                  </div>
                  <div
                    className="text-sm font-bold"
                    style={{ color: "#334155" }}
                  >
                    Latest Result
                  </div>
                  <div
                    className="text-sm font-bold"
                    style={{ color: "#334155" }}
                  >
                    Schedule
                  </div>
                  <div
                    className="text-sm font-bold"
                    style={{ color: "#334155" }}
                  >
                    Results From
                  </div>
                  <div
                    className="text-sm font-bold"
                    style={{ color: "#334155" }}
                  >
                    Trend (Last 5)
                  </div>
                </div>
              </div>

              {/* Data rows — own overflow-x-auto; scroll events sync the header above */}
              <div
                ref={dataScrollRef}
                className="overflow-x-auto"
                onScroll={handleDataScroll}
              >
                <div className="flex flex-col gap-3 pt-3">
                  {measures.map((measurement: ClinicalMeasurement, index) => {
                    // Derive completedHistory from longitudinalData
                    const completedHistory = measurement.longitudinalData
                      ? [...measurement.longitudinalData]
                          .sort(
                            (a, b) =>
                              new Date(b.date).getTime() -
                              new Date(a.date).getTime(),
                          )
                          .slice(0, 5)
                          .map((entry) => ({
                            date: entry.date,
                            responder: entry.responder,
                          }))
                      : [];

                    return (
                      <DataRowCard
                        key={index}
                        measureName={measurement.measureName}
                        status={measurement.status}
                        lastCompleted={measurement.lastCompleted}
                        nextDue={measurement.nextDue}
                        isOverdue={measurement.isOverdue}
                        onViewDetails={() =>
                          handleViewDetails(measurement.measureName)
                        }
                        subscales={measurement.subscales}
                        subscaleTrendData={measurement.subscaleTrendData}
                        displaySubscaleNames={measurement.displaySubscaleNames}
                        completedHistory={completedHistory}
                        longitudinalData={measurement.longitudinalData}
                        statusExplanation={measurement.statusExplanation}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Detail View with Multi-Pane Support */}
        {openTabs.length > 0 && (
          <div className="mt-8" data-testid="mbc-pane-manager">
            <PaneManager
              tabs={openTabs}
              activeTabId={activeTabId}
              onTabsUpdate={handleTabsUpdate}
              globalDateRange={globalDateRange}
              measureDataMap={measureDataMap}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MeasurementDashboard;
